import React from 'react';
import { Box, Container, Typography, Grid, Paper, Link } from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import PricingNavbar from './components/PricingNavbar';
import { PrimaryFooter } from '../../components/ui/footer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { PrimaryButton } from '../../components/ui/button';
import heroSub1 from '../../Images/hero_sub1.png';
import heroSub2 from '../../Images/hero_sub2.png';
import heroSub3 from '../../Images/hero_sub3.png';
import question1 from '../../Images/question1.png';
import question2 from '../../Images/question2.png';
import question3 from '../../Images/question3.png';
import question4 from '../../Images/question4.png';

const PricingPage = () => {
  const navigate = useNavigate();

  const pricingPlans = [
    {
      name: 'Starter',
      subtitle: 'For individuals & growing teams',
      gradient: 'linear-gradient(142.58deg, #FEFEED 0%, rgba(254, 254, 237, 0) 100%)',
      borderColor: '#E6E6E6',
      features: [
        'Core ETL features',
        'Limited - Standard connectors',
        'Moderate data volumes',
        'Automated workflows',
        'Basic + Advanced Monitoring',
        'Community support',
      ],
      buttonText: 'Start Free Trial',
      buttonVariant: 'contained',
      buttonAction: () => navigate('/trial'),
    },
    {
      name: 'Growth',
      subtitle: 'For data-driven teams',
      gradient: 'linear-gradient(142.58deg, #E7FAF4 0%, rgba(231, 250, 244, 0) 100%)',
      borderColor: '#E6E6E6',
      features: [
        'All Growth features',
        'Streaming & CDC pipelines',
        'Role-based access',
        'Automated workflows',
        'Performance optimization',
        'Priority support',
      ],
      buttonText: 'Connect with us',
      buttonVariant: 'outlined',
      buttonAction: () => navigate('/contact'),
    },
    {
      name: 'Enterprise',
      subtitle: 'For large organizations',
      gradient: 'linear-gradient(142.58deg, #F7EDFF 0%, rgba(247, 237, 255, 0) 100%)',
      borderColor: '#E6E6E6',
      features: [
        'All Scale features',
        'Custom connectors',
        'Enterprise security & compliance',
        'Dedicated infrastructure',
        'Dedicated support & SLAs',
        'Architecture & onboarding assistance',
      ],
      buttonText: 'Connect with us',
      buttonVariant: 'outlined',
      buttonAction: () => navigate('/contact'),
    },
  ];

  const supportCards = [
    {
      image: question1,
      title: 'Explore Documentation',
      description: 'Setup guides, connectors, and best practices',
      linkText: 'View Docs →',
      linkAction: () => navigate('/documentation'),
    },
    {
      image: question2,
      title: 'Browse Help Center',
      description: 'Common questions and technical support',
      linkText: 'Get Help →',
      linkAction: () => navigate('/help'),
    },
    {
      image: question3,
      title: 'View Integration',
      description: 'See all supported connectors',
      linkText: 'View Integrations →',
      linkAction: () => navigate('/connector-marketplace'),
    },
    {
      image: question4,
      title: 'Connect with us',
      description: 'For enterprise and custom needs',
      linkText: 'Contact Us →',
      linkAction: () => navigate('/contact'),
    },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
      }}
    >
      <PricingNavbar />

      <Box
        sx={{
          background: 'linear-gradient(93.19deg, #679E7E 0.2%, #559D76 51.53%, #679E7E 102.87%)',
          position: 'relative',
          overflow: 'hidden',
          py: { xs: 4, md: 6, lg: 9 },
          px: { xs: 2, sm: 3, md: 4 },
          mt: { xs: 9, md: 10, lg: 12 },
        }}
      >

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h1"
              sx={{
                fontFamily: 'IBM Plex Sans, sans-serif',
                fontWeight: 600,
                fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2rem', lg: '2.5rem' },
                lineHeight: '100%',
                letterSpacing: '0%',
                textAlign: 'center',
                verticalAlign: 'middle',
                color: '#FFFFFF',
                mb: { xs: 1.5, md: 1.75, lg: 2 },
              }}
            >
              Pricing that scales with your data
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: { xs: '0.875rem', sm: '1rem', md: '1.0625rem', lg: '1.125rem' },
                lineHeight: '140%',
                letterSpacing: '0%',
                textAlign: 'center',
                color: '#FFFFFF',
                maxWidth: '800px',
                mx: 'auto',
                mb: { xs: 4, md: 5, lg: 6 },
                px: { xs: 1, sm: 0 },
              }}
            >
              Flexible plans built for startups, growing teams, and enterprise data platforms.
            </Typography>

            <Grid container spacing={1} sx={{ mt: { xs: 3, md: 3.5, lg: 4 } }}>
              {[
                { image: heroSub1, text: 'Build faster pipelines' },
                { image: heroSub2, text: 'Scale from batch to real-time' },
                { image: heroSub3, text: 'Enterprise-grade security' },
              ].map((feature, index) => (
                <Grid item xs={12} sm={4} key={index}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 1,
                    }}
                  >
                    <Box
                      component="img"
                      src={feature.image}
                      alt={feature.text}
                      sx={{
                        width: 'auto',
                        height: { xs: '36px', sm: '40px', md: '48px' },
                        objectFit: 'contain',
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500,
                        fontSize: { xs: '0.8125rem', sm: '1rem', md: '1.0625rem', lg: '1.125rem' },
                        lineHeight: '24px',
                        letterSpacing: '-0.31px',
                        textAlign: 'center',
                        color: '#FFFFFF',
                      }}
                    >
                      {feature.text}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      <Box
        sx={{
          py: { xs: 4, md: 6, lg: 9 },
          px: { xs: 2, sm: 3, md: 4 },
          backgroundColor: '#FFFFFF',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 3, md: 3.5, lg: 4 }}>
            {pricingPlans.map((plan, index) => (
              <Grid item xs={12} md={6} lg={4} key={plan.name}>
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      background: plan.gradient,
                      borderRadius: '8px',
                      p: { xs: 3, md: 4 },
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      border: plan.borderColor ? `1px solid ${plan.borderColor}` : '1px solid rgba(0, 0, 0, 0.05)',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'IBM Plex Sans, sans-serif',
                        fontWeight: 700,
                        fontSize: { xs: '0.9375rem', sm: '1.125rem', md: '1.1875rem', lg: '1.25rem' },
                        lineHeight: '32px',
                        letterSpacing: '0.07px',
                        textAlign: 'center',
                        color: '#101828',
                        mb: 1,
                      }}
                    >
                      {plan.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 400,
                        fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.9375rem', lg: '1rem' },
                        lineHeight: '20px',
                        letterSpacing: '-0.15px',
                        textAlign: 'center',
                        color: '#4A5565',
                        mb: { xs: 2, md: 2.5, lg: 3 },
                      }}
                    >
                      {plan.subtitle}
                    </Typography>

                    <Box sx={{ flex: 1, mb: 3 }}>
                      {plan.features.map((feature, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.5,
                            mb: 2,
                          }}
                        >
                          <CheckCircleIcon
                            sx={{
                              fontSize: 20,
                              color: '#10B981',
                              flexShrink: 0,
                              mt: 0.25,
                            }}
                          />
                          <Typography
                            sx={{
                              fontFamily: 'Inter, sans-serif',
                              fontWeight: 400,
                              fontSize: { xs: '0.75rem', sm: '0.84375rem', md: '0.859375rem', lg: '0.875rem' },
                              lineHeight: '20px',
                              letterSpacing: '-0.15px',
                              color: '#364153',
                            }}
                          >
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <PrimaryButton
                      variant={plan.buttonVariant}
                      onClick={plan.buttonAction}
                      sx={{
                        width: '100%',
                      }}
                    >
                      {plan.buttonText}
                    </PrimaryButton>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box
        sx={{
          py: { xs: 4, md: 6, lg: 9 },
          px: { xs: 2, sm: 3, md: 4 },
          backgroundColor: '#FFFFFF',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontFamily: 'IBM Plex Sans, sans-serif',
                fontWeight: 700,
                fontSize: { xs: '1.125rem', sm: '1.5rem', md: '1.6875rem', lg: '1.875rem' },
                lineHeight: '36px',
                letterSpacing: '0.4px',
                textAlign: 'center',
                color: '#101828',
                mb: 1,
              }}
            >
              Have more questions?
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: { xs: '0.875rem', sm: '1rem', md: '1.0625rem', lg: '1.125rem' },
                lineHeight: '28px',
                letterSpacing: '-0.44px',
                textAlign: 'center',
                color: '#4A5565',
                mb: { xs: 4, md: 5, lg: 6 },
                px: { xs: 1, sm: 0 },
              }}
            >
              We&apos;re here to help you find the right solution
            </Typography>

            <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
              {supportCards.map((card, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={index} sx={{ display: 'flex' }}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    style={{ width: '100%', display: 'flex' }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 2, sm: 2.5, md: 3 },
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                        borderRadius: '8px',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <Box sx={{ mb: 2 }}>
                        <Box
                          component="img"
                          src={card.image}
                          alt={card.title}
                          sx={{
                            width: 'auto',
                            height: { xs: '32px', sm: '36px', md: '40px' },
                            objectFit: 'contain',
                          }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: 'IBM Plex Sans, sans-serif',
                          fontWeight: 600,
                          fontSize: { xs: '0.9375rem', sm: '1.0625rem', md: '1.09375rem', lg: '1.125rem' },
                          lineHeight: '27px',
                          letterSpacing: '-0.44px',
                          color: '#101828',
                          mb: 1,
                        }}
                      >
                        {card.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 400,
                          fontSize: { xs: '0.75rem', sm: '0.84375rem', md: '0.859375rem', lg: '0.875rem' },
                          lineHeight: '20px',
                          letterSpacing: '-0.15px',
                          color: '#4A5565',
                          mb: 2,
                          flex: 1,
                        }}
                      >
                        {card.description}
                      </Typography>
                      <Link
                        onClick={card.linkAction}
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 500,
                          fontSize: { xs: '0.75rem', sm: '0.84375rem', md: '0.859375rem', lg: '0.875rem' },
                          lineHeight: '20px',
                          letterSpacing: '-0.15px',
                          color: '#155DFC',
                          textDecoration: 'none',
                          cursor: 'pointer',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {card.linkText}
                      </Link>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      <Box
        sx={{
          py: { xs: 4, md: 6, lg: 9 },
          px: { xs: 2, sm: 3, md: 4 },
          background: 'linear-gradient(92.7deg, #EBFDFB -1.03%, #DAEFFF 104.87%)',
        }}
      >
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontFamily: 'IBM Plex Sans, sans-serif',
                fontWeight: 600,
                fontSize: { xs: '1.25rem', sm: '1.625rem', md: '1.8125rem', lg: '2rem' },
                lineHeight: '100%',
                letterSpacing: '0%',
                textAlign: 'center',
                verticalAlign: 'middle',
                color: '#101828',
                mb: { xs: 1.5, md: 1.75, lg: 2 },
              }}
            >
              Need something custom?
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: { xs: '0.875rem', sm: '1rem', md: '1.0625rem', lg: '1.125rem' },
                lineHeight: '140%',
                letterSpacing: '0%',
                textAlign: 'center',
                color: '#4A5565',
                mb: { xs: 3, md: 3.5, lg: 4 },
                px: { xs: 1, sm: 0 },
              }}
            >
              Talk to our team about enterprise needs, security, and tailored solutions.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <PrimaryButton
                variant="contained"
                onClick={() => navigate('/contact')}
              >
                Connect with us →
              </PrimaryButton>
            </Box>
          </motion.div>
        </Container>
      </Box>

      <PrimaryFooter />
    </Box>
  );
};

export default PricingPage;
